import { useState } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { Trans, useTranslation } from 'react-i18next';
import { LayoutGrid, List, Calendar, BarChart3, GripVertical, Tag, Users, CheckSquare, SlidersHorizontal, TableProperties, TrendingUp, Trophy, Sparkles, CalendarDays, Scissors, Star } from "lucide-react";
import { AnimatedKanbanHero } from "./AnimatedKanbanHero";
import { AnimatedListPreview } from "./AnimatedListPreview";
import { AnimatedCalendarPreview } from "./AnimatedCalendarPreview";
import { AnimatedAnalyticsPreview } from "./AnimatedAnalyticsPreview";

interface BulletConfig {
    icon: React.ReactNode;
    iconBg: string;
    line1: React.ReactNode;
    line2: string;
}

interface TabConfig {
    id: string;
    label: string;
    icon: React.ReactNode;
    title: string;
    bullets: BulletConfig[];
    bgColor: string;
    tabBg: string;
    preview: React.ReactNode;
    glowClass: string;
}

const tabs: TabConfig[] = [
    {
        id: "kanban",
        label: 'landing.showcase.kanban.label',
        icon: <LayoutGrid className="size-4" />,
        title: 'landing.showcase.kanban.title',
        bullets: [
            {
                icon: <GripVertical className="size-4 text-emerald-600" />,
                iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
                line1: <Trans i18nKey="landing.showcase.kanban.b1line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.kanban.b1line2',
            },
            {
                icon: <Tag className="size-4 text-emerald-600" />,
                iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
                line1: <Trans i18nKey="landing.showcase.kanban.b2line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.kanban.b2line2',
            },
            {
                icon: <Users className="size-4 text-emerald-600" />,
                iconBg: "bg-emerald-100 dark:bg-emerald-900/40",
                line1: <Trans i18nKey="landing.showcase.kanban.b3line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.kanban.b3line2',
            },
        ],
        bgColor: "bg-emerald-50 dark:bg-emerald-950/30",
        tabBg: "bg-emerald-200/60 dark:bg-emerald-900/50",
        preview: <AnimatedKanbanHero />,
        glowClass: "from-emerald-400 via-teal-500 to-green-600",
    },
    {
        id: "list",
        label: 'landing.showcase.list.label',
        icon: <List className="size-4" />,
        title: 'landing.showcase.list.title',
        bullets: [
            {
                icon: <CheckSquare className="size-4 text-purple-600" />,
                iconBg: "bg-purple-100 dark:bg-purple-900/40",
                line1: <Trans i18nKey="landing.showcase.list.b1line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.list.b1line2',
            },
            {
                icon: <SlidersHorizontal className="size-4 text-purple-600" />,
                iconBg: "bg-purple-100 dark:bg-purple-900/40",
                line1: <Trans i18nKey="landing.showcase.list.b2line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.list.b2line2',
            },
            {
                icon: <TableProperties className="size-4 text-purple-600" />,
                iconBg: "bg-purple-100 dark:bg-purple-900/40",
                line1: <Trans i18nKey="landing.showcase.list.b3line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.list.b3line2',
            },
        ],
        bgColor: "bg-purple-50 dark:bg-purple-950/30",
        tabBg: "bg-purple-200/60 dark:bg-purple-900/50",
        preview: <AnimatedListPreview />,
        glowClass: "from-purple-400 via-fuchsia-500 to-purple-600",
    },
    {
        id: "calendar",
        label: 'landing.showcase.calendar.label',
        icon: <Calendar className="size-4" />,
        title: 'landing.showcase.calendar.title',
        bullets: [
            {
                icon: <CalendarDays className="size-4 text-rose-600" />,
                iconBg: "bg-rose-100 dark:bg-rose-900/40",
                line1: <Trans i18nKey="landing.showcase.calendar.b1line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.calendar.b1line2',
            },
            {
                icon: <Scissors className="size-4 text-rose-600" />,
                iconBg: "bg-rose-100 dark:bg-rose-900/40",
                line1: <Trans i18nKey="landing.showcase.calendar.b2line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.calendar.b2line2',
            },
            {
                icon: <Star className="size-4 text-rose-600" />,
                iconBg: "bg-rose-100 dark:bg-rose-900/40",
                line1: <Trans i18nKey="landing.showcase.calendar.b3line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.calendar.b3line2',
            },
        ],
        bgColor: "bg-rose-50 dark:bg-rose-950/30",
        tabBg: "bg-rose-200/60 dark:bg-rose-900/50",
        preview: <AnimatedCalendarPreview />,
        glowClass: "from-rose-400 via-red-500 to-orange-500",
    },
    {
        id: "analytics",
        label: 'landing.showcase.analytics.label',
        icon: <BarChart3 className="size-4" />,
        title: 'landing.showcase.analytics.title',
        bullets: [
            {
                icon: <TrendingUp className="size-4 text-blue-600" />,
                iconBg: "bg-blue-100 dark:bg-blue-900/40",
                line1: <Trans i18nKey="landing.showcase.analytics.b1line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.analytics.b1line2',
            },
            {
                icon: <Trophy className="size-4 text-blue-600" />,
                iconBg: "bg-blue-100 dark:bg-blue-900/40",
                line1: <Trans i18nKey="landing.showcase.analytics.b2line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.analytics.b2line2',
            },
            {
                icon: <Sparkles className="size-4 text-blue-600" />,
                iconBg: "bg-blue-100 dark:bg-blue-900/40",
                line1: <Trans i18nKey="landing.showcase.analytics.b3line1" components={{ b: <strong /> }} />,
                line2: 'landing.showcase.analytics.b3line2',
            },
        ],
        bgColor: "bg-blue-50 dark:bg-blue-950/30",
        tabBg: "bg-blue-200/60 dark:bg-blue-900/50",
        preview: <AnimatedAnalyticsPreview />,
        glowClass: "from-blue-400 via-cyan-500 to-indigo-600",
    },
];

export const ViewShowcase = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("kanban");
    const activeConfig = tabs.find((t) => t.id === activeTab)!;

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Heading */}
                <motion.div
                    className="text-center mb-10"
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
                        View your content in{" "}
                        <span className="text-primary-600 dark:text-primary-400">different ways</span>
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                        Switch between views to plan, organize, schedule, and analyze your content — all from one place.
                    </p>
                </motion.div>

                {/* Tab Bar */}
                <motion.div
                    className="flex justify-center mb-10"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 }}
                >
                    <div className="relative inline-flex bg-white/50 dark:bg-[#0B0E14]/50 backdrop-blur-md rounded-full p-1.5 gap-1.5 border border-gray-200/50 dark:border-gray-800/50 shadow-sm">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`relative flex items-center justify-center gap-2.5 px-6 py-2.5 rounded-full text-base font-semibold transition-colors duration-200 ${
                                    activeTab === tab.id
                                        ? `text-gray-900 dark:text-white`
                                        : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                }`}
                            >
                                {activeTab === tab.id && (
                                    <motion.div
                                        layoutId="viewTabBg"
                                        className={`absolute inset-0 rounded-full ${tab.tabBg}`}
                                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                                    />
                                )}
                                <span className="relative z-10 flex items-center gap-2.5">
                                    {tab.icon}
                                    <span className="hidden sm:inline">{t(tab.label)}</span>
                                </span>
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Content Area */}
                <div className="relative">
                    {/* Ambient Glow */}
                    <div className="absolute inset-0 z-0 overflow-visible pointer-events-none">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab + '-glow1'}
                                className={`absolute -top-[20%] -left-[10%] w-[60%] h-[120%] bg-gradient-to-br ${activeConfig.glowClass} blur-[100px] opacity-20 dark:opacity-30 rounded-full mix-blend-multiply dark:mix-blend-screen`}
                                initial={{ opacity: 0, scale: 0.8, x: -50 }}
                                animate={{ opacity: 0.25, scale: 1, x: 0 }}
                                exit={{ opacity: 0, scale: 1.1, x: -50 }}
                                transition={{ duration: 0.8, ease: "easeInOut" }}
                            />
                            <motion.div
                                key={activeTab + '-glow2'}
                                className={`absolute -bottom-[20%] -right-[10%] w-[60%] h-[120%] bg-gradient-to-tl ${activeConfig.glowClass} blur-[100px] opacity-20 dark:opacity-30 rounded-full mix-blend-multiply dark:mix-blend-screen`}
                                initial={{ opacity: 0, scale: 0.8, x: 50 }}
                                animate={{ opacity: 0.25, scale: 1, x: 0 }}
                                exit={{ opacity: 0, scale: 1.1, x: 50 }}
                                transition={{ duration: 0.8, ease: "easeInOut", delay: 0.1 }}
                            />
                        </AnimatePresence>
                    </div>

                    <motion.div
                        className="relative z-10 rounded-[2.5rem] bg-white/70 dark:bg-[#0B0E14]/70 backdrop-blur-3xl border border-white/50 dark:border-gray-700/50 shadow-[0_8px_30px_rgb(0,0,0,0.06)] dark:shadow-[0_20px_80px_rgba(0,0,0,0.6)] p-6 sm:p-10 lg:p-14"
                        layout
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className="grid lg:grid-cols-[1fr_2fr] gap-8 lg:gap-12 items-center">
                        {/* Left: Description */}
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab + '-text'}
                                className="space-y-8 min-w-0"
                                initial={{ opacity: 0, x: -16 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 16 }}
                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight whitespace-pre-line tracking-tight">
                                    {t(activeConfig.title)}
                                </h3>

                                <div className="space-y-5">
                                    {activeConfig.bullets.map((bullet, i) => (
                                        <motion.div
                                            key={i}
                                            className="flex items-start gap-4"
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: i * 0.08, duration: 0.35 }}
                                        >
                                            <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${bullet.iconBg} flex items-center justify-center mt-0.5`}>
                                                {bullet.icon}
                                            </div>
                                            <div>
                                                <p className="text-base text-gray-800 dark:text-gray-200 leading-relaxed">
                                                    {bullet.line1}
                                                </p>
                                                {bullet.line2 && (
                                                    <p className="text-base text-gray-800 dark:text-gray-200 leading-relaxed">
                                                        <strong>{t(bullet.line2)}</strong>
                                                    </p>
                                                )}
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </motion.div>
                        </AnimatePresence>

                        {/* Right: Animated Preview */}
                        <div className="relative min-w-0 overflow-hidden h-[250px] sm:h-[350px] lg:h-[480px] rounded-2xl shadow-[0_0_0_1px_rgba(0,0,0,0.05)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.1)] bg-gray-50/50 dark:bg-[#0B0E14]/80">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeTab + '-preview'}
                                    className="absolute lg:relative top-0 left-0 w-[900px] lg:w-full h-[480px] lg:h-full origin-top-left scale-[35%] sm:scale-[60%] lg:scale-100"
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.96 }}
                                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {activeConfig.preview}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
